import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, Mail, Shield } from 'lucide-react';
import { useAuth } from '../App';

export default function UserMenu() {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close if clicking outside both button and dropdown
      if (
        isOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
    navigate('/login');
  };

  // Get user initials for avatar
  const getInitials = (name) => {
    if (!name) return 'U';
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  if (!user) return null;

  return (
    <div className="relative">
      {/* Avatar Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 p-2 hover:bg-white/5 rounded-lg transition-colors"
        data-testid="user-menu-button"
      >
        {/* Avatar Circle */}
        <div className="relative">
          <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#F59E0B] rounded-full flex items-center justify-center font-['Outfit'] font-bold text-[#0A1128]">
            {getInitials(user.name)}
          </div>
          {/* Online indicator */}
          <div className="absolute bottom-0 right-0 w-3 h-3 bg-[#10B981] border-2 border-[#0F172A] rounded-full"></div>
        </div>

        {/* User info - hidden on mobile */}
        <div className="hidden md:block text-left">
          <div className="text-sm font-medium text-white">{user.name}</div>
          <div className="text-xs text-[#94A3B8]">{user.role === 'admin' ? 'Administrator' : 'Investor'}</div>
        </div>
      </button>

      {/* Dropdown Menu - Absolute positioned overlay */}
      {isOpen && (
        <div
          ref={menuRef}
          className="absolute top-[60px] right-0 w-[260px] rounded-[14px] border overflow-hidden"
          style={{
            zIndex: 9999,
            background: 'rgba(10, 25, 60, 0.85)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            borderColor: 'rgba(255, 255, 255, 0.08)',
            boxShadow: '0 25px 60px rgba(0, 0, 0, 0.65)',
            animation: 'slideDown 0.18s ease-out'
          }}
        >
          {/* User Profile Section */}
          <div className="p-4">
            {/* Avatar + Name */}
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37] to-[#F59E0B] rounded-full flex items-center justify-center font-['Outfit'] font-bold text-[#0A1128] text-lg flex-shrink-0">
                {getInitials(user.name)}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-['Outfit'] font-bold text-white text-sm truncate">{user.name}</h3>
                <p className="text-xs text-[#94A3B8] flex items-center gap-1">
                  {user.role === 'admin' ? (
                    <>
                      <Shield className="w-3 h-3 flex-shrink-0" />
                      <span>Administrator</span>
                    </>
                  ) : (
                    <>
                      <User className="w-3 h-3 flex-shrink-0" />
                      <span>Investor</span>
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-center gap-2 text-xs text-[#94A3B8] mb-1">
              <Mail className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{user.email}</span>
            </div>

            {/* Phone (if available) */}
            {user.phone && (
              <div className="flex items-center gap-2 text-xs text-[#94A3B8]">
                <User className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{user.phone}</span>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="h-px bg-white/10"></div>

          {/* Logout Button */}
          <div className="p-2">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[#EF4444]/10 rounded-lg transition-colors group"
              data-testid="logout-button"
            >
              <div className="w-8 h-8 bg-[#EF4444]/20 rounded-lg flex items-center justify-center group-hover:bg-[#EF4444]/30 transition-colors flex-shrink-0">
                <LogOut className="w-4 h-4 text-[#EF4444]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white">Log Out</div>
                <div className="text-xs text-[#94A3B8]">Sign out of your account</div>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Animation Keyframes */}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
